// Shared, module-level tracker for in-flight segment-palette generation.
//
// Why module-level (not a ref): the builder mounts `useSegmentPalettes` in
// several places (SegmentSettings, ColorPaletteModal, the page). Each instance
// has its OWN `inFlightRef`, so no single ref can tell the autosave timer
// whether ANY palette generate/save is still pending. A tiny shared counter
// closes that gap so the 30s autosave (LW-02) can skip a tick while a palette
// write is mid-flight, instead of persisting a stale/half-written palette state
// (last-write-wins with no version guard). Reversible: if the flag that gates
// the autosave skip is off, this counter is simply ignored.
//
// Claude · palette-stabilization · 2026-06-13

import { useSyncExternalStore } from "react";

let inFlightCount = 0;
const listeners = new Set();

const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn(inFlightCount);
    } catch (e) {
      // a listener throwing must not corrupt the counter
      // eslint-disable-next-line no-console
      console.error("paletteInFlight listener error:", e);
    }
  });
};

// review-0921-fe-5955-followups NB-1 (staging port of dev PR #5957):
// generateScheme/savePalette (services/api.js) carry no axios timeout, so a
// request that never settles leaks the begin() below forever -- every
// autosave gate reads paletteInFlight and returns false unconditionally
// while it's true, so one leaked begin is a silent no-save for the rest of
// the page session. A begin older than WATCHDOG_MS is released exactly once
// (a real late `finally { endPaletteWork() }` after that point is a no-op
// against an already-empty queue, still floored at 0) and logged once.
const WATCHDOG_MS = 30000;
// FIFO of pending watchdog timers, one per outstanding begin. The counter is a
// fungible aggregate (not per-request), so it doesn't matter WHICH begin a
// given end/watchdog corresponds to -- only that counts of begins and
// releases (end or watchdog) stay matched.
const pendingWatchdogs = [];

const releaseStale = (timerId) => {
  const idx = pendingWatchdogs.indexOf(timerId);
  if (idx === -1) return; // already cleared by a matching endPaletteWork
  pendingWatchdogs.splice(idx, 1);
  inFlightCount = Math.max(0, inFlightCount - 1);
  // eslint-disable-next-line no-console
  console.warn(
    `paletteInFlight watchdog: released a palette begin() that never ended after ${WATCHDOG_MS}ms (leaked generateScheme/savePalette request)`
  );
  notify();
};

export const beginPaletteWork = () => {
  inFlightCount += 1;
  const timerId = setTimeout(() => releaseStale(timerId), WATCHDOG_MS);
  pendingWatchdogs.push(timerId);
  notify();
};

export const endPaletteWork = () => {
  const timerId = pendingWatchdogs.shift();
  if (timerId !== undefined) clearTimeout(timerId);
  inFlightCount = Math.max(0, inFlightCount - 1);
  notify();
};

export const isPaletteWorkInFlight = () => inFlightCount > 0;

// Optional subscription hook for components that want to react to the count.
export const subscribePaletteInFlight = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// ISP-9999 (editwheel-collab-1 rework): a REACTIVE read of the in-flight flag.
// `isPaletteWorkInFlight()` alone is a plain module-level read — calling it
// inside a `useEffect` body only ever sees its value at the instant that
// effect happens to run. If a palette generate+save round-trip is in flight
// at that instant (which it always is right after add-segment: the new
// segment auto-derives a colour palette on select), the effect reads `true`,
// backs off, and nothing ever tells the component the count later dropped to
// 0 — there's no re-render/re-run to retry on. `useSyncExternalStore` gives a
// value that actually changes across renders when `beginPaletteWork` /
// `endPaletteWork` fire, so a `useEffect` depending on it re-runs when the
// palette work that deferred it finishes.
export const usePaletteWorkInFlight = () =>
  useSyncExternalStore(
    subscribePaletteInFlight,
    isPaletteWorkInFlight,
    isPaletteWorkInFlight,
  );
